import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { ScanLine, Smartphone, Monitor, Loader2, Copy, Check, RefreshCw, UploadCloud, Car, FileText, RotateCcw } from "lucide-react";

const fireConfetti = () => {
  try { confetti({ particleCount: 80, spread: 70, origin: { y: 0.4 }, colors: ["#00E5FF", "#8B5CF6", "#00E5A0"] }); } catch (_) {}
};

const DOC_TYPES = [
  { id: "carte_grise", label: "Carte grise", Icon: Car },
  { id: "invoice", label: "Facture / Bon de commande", Icon: FileText },
];

export const CarteGriseScanner = ({ onExtract }) => {
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState("carte_grise");
  const [tab, setTab] = useState("phone");
  const [session, setSession] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [waitingPhone, setWaitingPhone] = useState(false);
  const [review, setReview] = useState(null);
  const [vat, setVat] = useState(null); // null | "checking" | {valid, name}
  const vatTimer = useRef(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const createSession = async () => {
    setCreating(true);
    stopPoll();
    try {
      const { data } = await api.post("/scan/session", { origin: window.location.origin, doc_type: docType });
      setSession(data);
      setWaitingPhone(true);
      pollRef.current = setInterval(async () => {
        try {
          const { data: p } = await api.get(`/scan/session/${data.token}`);
          if (p.status === "done" && p.result) { stopPoll(); setWaitingPhone(false); setReview(p.result); fireConfetti(); }
          else if (p.status === "error") { stopPoll(); setWaitingPhone(false); toast.error("Photo illisible — reprenez-la depuis le téléphone"); }
        } catch (_) {}
      }, 2000);
    } catch (e) {
      toast.error("Impossible de démarrer le scan");
    } finally {
      setCreating(false);
    }
  };

  const uploadHere = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", docType);
      const { data } = await api.post("/scan/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReview(data);
      fireConfetti();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lecture impossible — reprenez une photo nette");
    } finally {
      setUploading(false);
    }
  };

  const setField = (k, v) => setReview((r) => ({ ...r, [k]: v }));

  const checkVat = (value) => {
    clearTimeout(vatTimer.current);
    const v = (value || "").trim();
    if (!v || v.length < 4) { setVat(null); return; }
    setVat("checking");
    vatTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/vat/check", { vat: v });
        setVat({ valid: !!data.valid, name: data.name || "" });
      } catch (_) { setVat(null); }
    }, 500);
  };

  // Auto-check the VAT number when an invoice review appears.
  useEffect(() => {
    if (review?.doc_type === "invoice") checkVat(review.vat_number);
    else setVat(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review?.doc_type]);

  const confirmReview = () => {
    const bits = review.doc_type === "invoice"
      ? [review.company_name, review.city].filter(Boolean).join(" · ")
      : [review.brand, review.model, review.year].filter(Boolean).join(" ");
    onExtract?.(review);
    toast.success(`Appliqué : ${bits || "document reconnu"}`);
    close();
  };

  const retry = () => {
    setReview(null);
    if (tab === "phone") createSession();
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(session.mobile_url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
  };

  const close = () => { stopPoll(); setOpen(false); setSession(null); setWaitingPhone(false); setReview(null); setTab("phone"); };

  useEffect(() => {
    if (open && !review && tab === "phone") createSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, docType]);

  useEffect(() => () => stopPoll(), []);

  const isInvoice = docType === "invoice";

  return (
    <>
      <button type="button" data-testid="scan-cg-btn" onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-sm border border-aurora-cyan/50 bg-aurora-cyan/10 text-aurora-cyan font-display font-bold uppercase tracking-wider text-xs hover:bg-aurora-cyan/20 transition-colors">
        <ScanLine className="w-4 h-4" /> Scanner un document
      </button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="max-w-md popover-solid border-white/10" data-testid="scan-cg-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-aurora-cyan" /> Scanner un document
            </DialogTitle>
            <DialogDescription className="font-mono text-[11px] text-zinc-500">
              Reconnaissance IA — carte grise (véhicule) ou facture (infos client).
            </DialogDescription>
          </DialogHeader>

          {/* Document type */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {DOC_TYPES.map(({ id, label, Icon }) => (
              <button key={id} data-testid={`scan-doctype-${id}`} onClick={() => { setReview(null); setDocType(id); }}
                className={`flex items-center justify-center gap-2 py-2 rounded-sm text-[11px] font-mono uppercase tracking-wider transition-colors border text-center ${docType === id ? "border-aurora-purple bg-aurora-purple/10 text-aurora-purple" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                <Icon className="w-4 h-4 shrink-0" /> {label}
              </button>
            ))}
          </div>

          {review ? (
            /* ---- Aperçu recadrage + confirmation ---- */
            <div data-testid="scan-review">
              <div className="rounded-md overflow-hidden border border-white/10 bg-black/40 mb-3">
                <img src={review.preview} alt="Document recadré" className="w-full max-h-56 object-contain" data-testid="scan-review-image" />
              </div>
              <div className="space-y-2 mb-4">
                {isInvoice ? (
                  <>
                    {[["company_name", "Société"], ["address_line", "Adresse"], ["postal_code", "Code postal"], ["city", "Ville"], ["country", "Pays"], ["order_ref", "Réf. commande"]].map(([k, label]) => (
                      <div key={k}>
                        <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1 block">{label}</label>
                        <input data-testid={`scan-field-${k}`} value={review[k] || ""} onChange={(e) => setField(k, e.target.value)}
                          className="w-full bg-black/50 border border-white/12 text-white px-2.5 py-1.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none" />
                      </div>
                    ))}
                    <div>
                      <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1 block flex items-center gap-2">
                        N° TVA
                        {vat === "checking" && <span data-testid="vat-checking" className="normal-case tracking-normal text-zinc-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> vérification…</span>}
                        {vat && vat !== "checking" && vat.valid && <span data-testid="vat-valid" className="normal-case tracking-normal text-signal-success flex items-center gap-1"><Check className="w-3 h-3" /> TVA valide</span>}
                        {vat && vat !== "checking" && !vat.valid && <span data-testid="vat-invalid" className="normal-case tracking-normal text-signal-error">non vérifiée</span>}
                      </label>
                      <input data-testid="scan-field-vat_number" value={review.vat_number || ""} onChange={(e) => { setField("vat_number", e.target.value); checkVat(e.target.value); }}
                        className={`w-full bg-black/50 border text-white px-2.5 py-1.5 rounded-sm text-sm focus:outline-none ${vat && vat !== "checking" && vat.valid ? "border-signal-success/60" : "border-white/12 focus:border-aurora-cyan"}`} />
                      {vat && vat !== "checking" && vat.valid && vat.name && <div className="font-mono text-[10px] text-signal-success mt-1 truncate">✓ {vat.name}</div>}
                    </div>
                  </>
                ) : (
                  [["brand", "Marque"], ["model", "Modèle"], ["year", "Année"], ["engine_label", "Moteur"]].map(([k, label]) => (
                    <div key={k}>
                      <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1 block">{label}</label>
                      <input data-testid={`scan-field-${k}`} value={review[k] || ""} onChange={(e) => setField(k, e.target.value)}
                        className="w-full bg-black/50 border border-white/12 text-white px-2.5 py-1.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none" />
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <button data-testid="scan-review-confirm" onClick={confirmReview}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-sm bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-xs hover:bg-cyan-300 transition-colors">
                  <Check className="w-4 h-4" /> Confirmer
                </button>
                <button data-testid="scan-review-retry" onClick={retry}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm border border-white/15 text-zinc-300 font-display font-bold uppercase tracking-wider text-xs hover:border-white/30 transition-colors">
                  <RotateCcw className="w-4 h-4" /> Reprendre
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-2 mb-4">
                <button data-testid="scan-tab-phone" onClick={() => setTab("phone")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm text-xs font-mono uppercase tracking-wider transition-colors border ${tab === "phone" ? "border-aurora-cyan bg-aurora-cyan/10 text-aurora-cyan" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                  <Smartphone className="w-4 h-4" /> Téléphone
                </button>
                <button data-testid="scan-tab-pc" onClick={() => { stopPoll(); setWaitingPhone(false); setTab("pc"); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm text-xs font-mono uppercase tracking-wider transition-colors border ${tab === "pc" ? "border-aurora-cyan bg-aurora-cyan/10 text-aurora-cyan" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                  <Monitor className="w-4 h-4" /> Cet ordinateur
                </button>
              </div>

              {tab === "phone" && (
                <div className="text-center">
                  {creating || !session ? (
                    <div className="py-12 flex flex-col items-center gap-3 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin text-aurora-cyan" /><span className="font-mono text-xs">Génération du lien sécurisé…</span></div>
                  ) : (
                    <>
                      <p className="font-mono text-[11px] text-zinc-400 mb-4 leading-relaxed">Scannez ce QR code avec votre téléphone, prenez la photo,<br />le formulaire se remplira ici.</p>
                      <div className="inline-block p-3 rounded-md bg-white" data-testid="scan-qr"><img src={session.qr} alt="QR code" className="w-48 h-48" /></div>
                      <div className="mt-4 flex items-center gap-2 justify-center">
                        <code className="font-mono text-[10px] text-zinc-500 truncate max-w-[220px]">{session.mobile_url}</code>
                        <button data-testid="scan-copy-link" onClick={copyLink} className="text-zinc-400 hover:text-aurora-cyan transition-colors">{copied ? <Check className="w-4 h-4 text-signal-success" /> : <Copy className="w-4 h-4" />}</button>
                      </div>
                      {waitingPhone && (
                        <div className="mt-4 flex items-center justify-center gap-2 text-aurora-cyan font-mono text-[11px] uppercase tracking-widest" data-testid="scan-waiting">
                          <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-aurora-cyan opacity-70" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-aurora-cyan" /></span>
                          En attente de la photo du téléphone…
                        </div>
                      )}
                      <button onClick={createSession} className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-aurora-cyan transition-colors"><RefreshCw className="w-3 h-3" /> Nouveau lien</button>
                    </>
                  )}
                </div>
              )}

              {tab === "pc" && (
                <div>
                  <p className="font-mono text-[11px] text-zinc-400 mb-4 text-center">Choisissez une photo {isInvoice ? "de la facture" : "de la carte grise"} déjà présente sur cet ordinateur.</p>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="scan-pc-input" onChange={(e) => uploadHere(e.target.files?.[0])} />
                  <button data-testid="scan-pc-pick" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="w-full flex flex-col items-center gap-3 py-10 rounded-md border-2 border-dashed border-aurora-cyan/40 bg-aurora-cyan/5 hover:border-aurora-cyan transition-colors disabled:opacity-60">
                    {uploading ? <Loader2 className="w-8 h-8 animate-spin text-aurora-cyan" /> : <UploadCloud className="w-8 h-8 text-aurora-cyan" />}
                    <span className="font-mono text-xs text-zinc-300">{uploading ? "Analyse en cours…" : "Choisir une image"}</span>
                  </button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
